import React, { useState, useEffect } from "react";
import { CircularProgress } from "@mui/material";
import SpDialog from "../../../globals/SpDialog";
import { getDocs } from "../../../globals/services";

const MoveItemDialog = ({
  open,
  onClose,
  item,
  firebase,
  onMove,
  currentTab,
  currentParent, // Add current parent to props
  isMainItem = false, // New prop to indicate if this is a main item move
}) => {
  const [targetTab, setTargetTab] = useState("");
  const [targetParent, setTargetParent] = useState(null);
  const [availableParents, setAvailableParents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingParents, setLoadingParents] = useState(false);

  // New collection state (for sectors moving to categories or becoming main sectors)
  const [createNewCollection, setCreateNewCollection] = useState(false);
  const [newCollectionName, setNewCollectionName] = useState("");

  // Sector-specific state
  const [makeMasterSector, setMakeMasterSector] = useState(false);

  // Error handling state
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // NEW: State for creating new parent items
  const [createNewParent, setCreateNewParent] = useState(false);
  const [newParentName, setNewParentName] = useState("");
  const [newParentDescription, setNewParentDescription] = useState("");

  // NEW: State for creating new sectors
  const [createNewSector, setCreateNewSector] = useState(false);
  const [newSectorName, setNewSectorName] = useState("");
  const [newSectorDescription, setNewSectorDescription] = useState("");

  // NEW: State for hierarchical sector/industry selection
  const [selectedSector, setSelectedSector] = useState(null);
  const [availableSectors, setAvailableSectors] = useState([]);
  const [availableIndustries, setAvailableIndustries] = useState([]);
  const [loadingSectors, setLoadingSectors] = useState(false);
  const [loadingIndustries, setLoadingIndustries] = useState(false);

  // Collection type mapping
  const collectionMapping = {
    stacks: {
      label: "Stacks",
      collection: "techStacks150r",
      subCollection: "technologies",
    },
    sectors: {
      label: "Sectors",
      collection: isMainItem ? "sectors" : "sectors", // For main items, target is sectors collection
      subCollection: "industry", // Industry items within sectors
    },
    categories: {
      label: "Categories",
      collection: "categories",
      subCollection: "subcollections",
    },
  };

  // Get all available collections from all tabs
  const allCollectionTypes = Object.keys(collectionMapping);

  // Reset states when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setTargetTab("");
      setTargetParent(null);
      setAvailableParents([]);
      setCreateNewCollection(false);
      setNewCollectionName("");
      setMakeMasterSector(false);
      setError("");
      setSuccess("");
      // NEW: Reset new state variables
      setCreateNewParent(false);
      setNewParentName("");
      setNewParentDescription("");
      setCreateNewSector(false);
      setNewSectorName("");
      setNewSectorDescription("");
      setSelectedSector(null);
      setAvailableSectors([]);
      setAvailableIndustries([]);
    } else {
      // Clear messages when opening
      setError("");
      setSuccess("");
    }
  }, [open]);

  // Fetch available parent collections when target tab changes or current parent changes
  useEffect(() => {
    if (targetTab) {
      if (targetTab === "sectors") {
        fetchAvailableSectors();
      } else {
        fetchAvailableParents();
      }
    } else {
      setAvailableParents([]);
      setTargetParent(null);
      setSelectedSector(null);
      setAvailableSectors([]);
      setAvailableIndustries([]);
    }
  }, [targetTab, currentParent]);

  // NEW: Fetch available sectors for hierarchical selection
  const fetchAvailableSectors = async () => {
    setLoadingSectors(true);
    try {
      const sectorsResult = await getDocs("sectors", false, 100, "name");
      const sectorsData = sectorsResult.docs.map((doc) => ({
        id: doc.id,
        name: doc.data().name,
        ref: doc.ref,
        data: doc.data(),
      }));
      setAvailableSectors(sectorsData);
    } catch (error) {
      console.error("Error fetching sectors:", error);
      setAvailableSectors([]);
    } finally {
      setLoadingSectors(false);
    }
  };

  // NEW: Fetch available industries within a selected sector
  const fetchAvailableIndustries = async (sectorRef) => {
    if (!sectorRef) return;

    setLoadingIndustries(true);
    try {
      const industryResult = await getDocs(
        `${sectorRef.path}/industry`,
        false,
        100,
        "name"
      );

      const industriesData = industryResult.docs.map((industryDoc) => ({
        id: industryDoc.id,
        name: industryDoc.data().name,
        ref: industryDoc.ref,
        data: industryDoc.data(),
      }));

      setAvailableIndustries(industriesData);
    } catch (error) {
      console.error("Error fetching industries:", error);
      setAvailableIndustries([]);
    } finally {
      setLoadingIndustries(false);
    }
  };

  // Effect to fetch industries when sector is selected
  useEffect(() => {
    if (selectedSector && targetTab === "sectors") {
      fetchAvailableIndustries(selectedSector.ref);
      // Reset industry selection when sector changes
      setTargetParent(null);
      setCreateNewParent(false);
    } else {
      setAvailableIndustries([]);
    }
  }, [selectedSector, targetTab]);

  const fetchAvailableParents = async () => {
    if (!targetTab) return;

    // For main items moving within the same tab type, handle differently
    if (isMainItem && targetTab === currentTab) {
      setAvailableParents([]);
      return;
    }

    // For main items moving to different tabs (except sectors), we don't need to fetch parents
    if (isMainItem && targetTab !== "sectors") {
      setAvailableParents([]);
      return;
    }

    setLoadingParents(true);
    try {
      const collectionConfig = collectionMapping[targetTab];

      let parentsData = [];

      if (targetTab === "sectors") {
        if (isMainItem) {
          // For main items moving TO sectors, show available main sectors
          const sectorsResult = await getDocs("sectors", false, 100, "name");
          parentsData = sectorsResult.docs.map((doc) => ({
            id: doc.id,
            name: doc.data().name,
            ref: doc.ref,
            data: doc.data(),
          }));
        } else {
          // For sub-items, we need to fetch all industry items from all sectors
          // First get all sectors
          const sectorsResult = await getDocs("sectors", false, 100, "name");

          // Then get all industry items from each sector
          const industryPromises = sectorsResult.docs.map(async (sectorDoc) => {
            try {
              const industryResult = await getDocs(
                `${sectorDoc.ref.path}/industry`,
                false,
                100,
                "name"
              );

              return industryResult.docs.map((industryDoc) => ({
                id: industryDoc.id,
                name: industryDoc.data().name,
                ref: industryDoc.ref,
                data: industryDoc.data(),
                sectorName: sectorDoc.data().name, // Include sector name for display
              }));
            } catch (error) {
              console.error(
                `Error fetching industry for sector ${sectorDoc.id}:`,
                error
              );
              return [];
            }
          });

          const industryArrays = await Promise.all(industryPromises);
          parentsData = industryArrays.flat();

          // Sort by sector type order first, then alphabetically within each sector
          const sectorOrder = {
            Primary: 1,
            Secondary: 2,
            Tertiary: 3,
            Quaternary: 4,
            Quinary: 5,
            Senary: 6,
            Septenary: 7,
            Octonary: 8,
            Nonary: 9,
            Denary: 10,
          };

          parentsData.sort((a, b) => {
            // First, sort by sector type order
            const sectorOrderA = sectorOrder[a.sectorName] || 999;
            const sectorOrderB = sectorOrder[b.sectorName] || 999;

            if (sectorOrderA !== sectorOrderB) {
              return sectorOrderA - sectorOrderB;
            }

            // If same sector type, sort alphabetically by industry name
            return a.name.localeCompare(b.name);
          });
        }
      } else {
        // For stacks and categories, use the main collection
        const result = await getDocs(
          collectionConfig.collection,
          false,
          100,
          "name"
        );

        parentsData = result.docs.map((doc) => ({
          id: doc.id,
          name: doc.data().name,
          ref: doc.ref,
          data: doc.data(),
        }));
      }

      // Filter out current parent to avoid moving to the same location
      if (currentParent) {
        parentsData = parentsData.filter(
          (parent) => parent.id !== currentParent.id
        );
      }

      setAvailableParents(parentsData);
    } catch (error) {
      console.error("Error fetching parents:", error);
      setAvailableParents([]);
    } finally {
      setLoadingParents(false);
    }
  };

  const handleMove = async () => {
    if (!targetTab || loading) return;

    // Clear previous messages
    setError("");
    setSuccess("");

    // Validation for main items vs sub-items
    if (isMainItem) {
      // Special validation for sectors
      if (currentTab === "sectors" && targetTab === "sectors") {
        if (!makeMasterSector && !targetParent) {
          setError(
            "Please either select a target sector or choose to make it a main sector"
          );
          return;
        }
      } else {
        // For main items moving to categories with new collection option
        if (targetTab === "categories" && createNewCollection) {
          if (!newCollectionName.trim()) {
            setError("Please enter a name for the new collection");
            return;
          }
        }
        // For main items, check if moving to the same tab (except sectors)
        if (targetTab === currentTab) {
          setError("Cannot move item to the same collection type");
          return;
        }
      }
    } else {
      // For sub-items, enhanced validation
      if (targetTab === "sectors") {
        // For sectors: need either sector selection OR create new sector
        if (!selectedSector && !createNewSector) {
          setError(
            "Please select a target sector or choose to create a new one"
          );
          return;
        }

        // If creating new sector, validate sector name
        if (createNewSector && !newSectorName.trim()) {
          setError("Please enter a name for the new sector");
          return;
        }

        // For industry selection: need either existing industry OR create new industry
        if (!targetParent && !createNewParent) {
          setError(
            "Please select a target industry or choose to create a new one"
          );
          return;
        }

        // If creating new industry, validate industry name
        if (createNewParent && !newParentName.trim()) {
          setError("Please enter a name for the new industry");
          return;
        }
      } else {
        // For non-sectors: standard validation
        if (!targetParent && !createNewParent) {
          setError(
            "Please select a target parent collection or choose to create a new one"
          );
          return;
        }

        // Validation for creating new parent
        if (createNewParent && !newParentName.trim()) {
          setError(
            `Please enter a name for the new ${
              targetTab === "categories" ? "category" : "collection"
            }`
          );
          return;
        }
      }

      // Check if moving to same parent
      if (
        targetParent &&
        currentParent &&
        targetParent.id === currentParent.id
      ) {
        setError("Cannot move item to the same parent collection");
        return;
      }
    }

    setLoading(true);
    try {
      await onMove({
        item,
        targetTab,
        targetParent,
        targetCollection: collectionMapping[targetTab],
        createNewCollection: isMainItem && createNewCollection,
        newCollectionName:
          isMainItem && createNewCollection
            ? newCollectionName.trim()
            : undefined,
        makeMasterSector: isMainItem && makeMasterSector,
        // NEW: Add parameters for creating new parent items
        createNewParent: !isMainItem && createNewParent,
        newParentName:
          !isMainItem && createNewParent ? newParentName.trim() : undefined,
        newParentDescription:
          !isMainItem && createNewParent ? newParentDescription : undefined,
        createNewSector:
          !isMainItem &&
          createNewParent &&
          targetTab === "sectors" &&
          createNewSector,
        newSectorName:
          !isMainItem &&
          createNewParent &&
          targetTab === "sectors" &&
          createNewSector
            ? newSectorName.trim()
            : undefined,
        newSectorDescription:
          !isMainItem &&
          createNewParent &&
          targetTab === "sectors" &&
          createNewSector
            ? newSectorDescription
            : undefined,
      });
      setSuccess(`Successfully moved ${item?.name}`);
      // Close after a brief delay to show success message
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      console.error("Error moving item:", error);
      setError(`Failed to move item: ${error.message || "Please try again"}`);
    } finally {
      setLoading(false);
    }
  };

  const availableTabs = allCollectionTypes;

  return (
    <SpDialog open={open} onClose={onClose} useCustomBox={true}>
      <div
        className="moveItemDialog"
        style={{
          maxHeight: "85vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <div className="moveItemDialog__header">
          <h2>
            {isMainItem ? "Move Collection" : "Move Item to Another Collection"}
          </h2>
          <p>
            Moving: <strong>{item?.name}</strong>
          </p>
          {!isMainItem && currentParent && (
            <p className="moveItemDialog__currentLocation">
              Currently in: <strong>{currentParent.name}</strong> (
              {collectionMapping[currentTab]?.label})
            </p>
          )}
          {isMainItem && (
            <p className="moveItemDialog__currentLocation">
              Currently in:{" "}
              <strong>{collectionMapping[currentTab]?.label}</strong>
            </p>
          )}
        </div>

        {/* Error and Success Messages */}
        {error && (
          <div
            className="moveItemDialog__error"
            style={{
              padding: "12px",
              margin: "8px 0",
              backgroundColor: "#fee",
              border: "1px solid #fcc",
              borderRadius: "4px",
              color: "#c33",
            }}
          >
            <strong>Error:</strong> {error}
          </div>
        )}

        {success && (
          <div
            className="moveItemDialog__success"
            style={{
              padding: "12px",
              margin: "8px 0",
              backgroundColor: "#efe",
              border: "1px solid #cfc",
              borderRadius: "4px",
              color: "#363",
            }}
          >
            <strong>Success:</strong> {success}
          </div>
        )}

        <div
          className="moveItemDialog__content"
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "0 4px", // Add small padding for scrollbar
            marginRight: "-4px", // Compensate for padding to maintain alignment
          }}
        >
          {/* Target Tab Selection */}
          <div className="moveItemDialog__field">
            <label>Target Collection Type:</label>
            <select
              value={targetTab}
              onChange={(e) => setTargetTab(e.target.value)}
              disabled={loading}
            >
              <option value="">Select collection type...</option>
              {availableTabs.map((tab) => (
                <option key={tab} value={tab}>
                  {collectionMapping[tab].label}
                </option>
              ))}
            </select>
          </div>

          {/* For non-sectors: Existing parent selection UI */}
          {targetTab && !isMainItem && targetTab !== "sectors" && (
            <div className="moveItemDialog__field">
              <label>
                {targetTab === "sectors"
                  ? "Target Industry:"
                  : "Target Parent Collection:"}
              </label>
              {loadingParents ? (
                <div className="moveItemDialog__loading">
                  <CircularProgress size={20} />
                  <span>Loading available parents...</span>
                </div>
              ) : (
                <select
                  value={targetParent?.id || ""}
                  onChange={(e) => {
                    const selectedParent = availableParents.find(
                      (parent) => parent.id === e.target.value
                    );
                    setTargetParent(selectedParent);
                    setCreateNewParent(false); // Reset create new option when selecting existing
                  }}
                  disabled={loading}
                >
                  <option value="">
                    {availableParents.length === 0
                      ? "No other collections available"
                      : `Select target ${
                          targetTab === "sectors"
                            ? "industry"
                            : "parent collection"
                        }...`}
                  </option>
                  {availableParents.map((parent) => (
                    <option key={parent.id} value={parent.id}>
                      {parent.name}
                    </option>
                  ))}
                </select>
              )}

              {/* Option to create new parent for non-sectors */}
              <div style={{ marginTop: "10px" }}>
                <label>
                  <input
                    type="checkbox"
                    checked={createNewParent}
                    onChange={(e) => {
                      setCreateNewParent(e.target.checked);
                      if (e.target.checked) {
                        setTargetParent(null); // Clear existing selection
                      }
                    }}
                    disabled={loading}
                  />
                  <span style={{ marginLeft: "8px" }}>
                    Create new{" "}
                    {targetTab === "categories" ? "category" : "collection"}
                  </span>
                </label>
              </div>

              {/* Input for new parent name and description */}
              {createNewParent && (
                <div
                  style={{
                    marginTop: "10px",
                    border: "1px solid #ddd",
                    padding: "10px",
                    borderRadius: "4px",
                  }}
                >
                  <div style={{ marginBottom: "10px" }}>
                    <label>Name:</label>
                    <input
                      type="text"
                      value={newParentName}
                      onChange={(e) => setNewParentName(e.target.value)}
                      placeholder={`Enter ${
                        targetTab === "categories" ? "category" : "collection"
                      } name`}
                      disabled={loading}
                      style={{ width: "100%", marginTop: "5px" }}
                    />
                  </div>
                  <div>
                    <label>Description:</label>
                    <textarea
                      value={newParentDescription}
                      onChange={(e) => setNewParentDescription(e.target.value)}
                      placeholder={`Enter ${
                        targetTab === "categories" ? "category" : "collection"
                      } description`}
                      disabled={loading}
                      rows={3}
                      style={{
                        width: "100%",
                        marginTop: "5px",
                        resize: "vertical",
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* For sectors: Enhanced hierarchical selection */}
          {targetTab && !isMainItem && targetTab === "sectors" && (
            <div className="moveItemDialog__field">
              {/* Step 1: Select or create sector */}
              <div>
                <label>Target Sector:</label>
                {loadingSectors ? (
                  <div className="moveItemDialog__loading">
                    <CircularProgress size={20} />
                    <span>Loading sectors...</span>
                  </div>
                ) : (
                  <select
                    value={selectedSector?.id || ""}
                    onChange={(e) => {
                      const selectedSect = availableSectors.find(
                        (sector) => sector.id === e.target.value
                      );
                      setSelectedSector(selectedSect);
                      setCreateNewSector(false); // Reset create new when selecting existing
                    }}
                    disabled={loading || createNewSector}
                  >
                    <option value="">
                      {availableSectors.length === 0
                        ? "No sectors available"
                        : "Select target sector..."}
                    </option>
                    {availableSectors.map((sector) => (
                      <option key={sector.id} value={sector.id}>
                        {sector.name}
                      </option>
                    ))}
                  </select>
                )}

                {/* Option to create new sector */}
                <div style={{ marginTop: "10px" }}>
                  <label>
                    <input
                      type="checkbox"
                      checked={createNewSector}
                      onChange={(e) => {
                        setCreateNewSector(e.target.checked);
                        if (e.target.checked) {
                          setSelectedSector(null); // Clear existing selection
                          setTargetParent(null);
                          setCreateNewParent(true); // Auto-enable create new industry
                        } else {
                          setCreateNewParent(false);
                        }
                      }}
                      disabled={loading}
                    />
                    <span style={{ marginLeft: "8px" }}>Create new sector</span>
                  </label>
                </div>

                {/* Input for new sector */}
                {createNewSector && (
                  <div
                    style={{
                      marginTop: "10px",
                      border: "1px solid #ddd",
                      padding: "10px",
                      borderRadius: "4px",
                    }}
                  >
                    <div style={{ marginBottom: "10px" }}>
                      <label>Sector Name:</label>
                      <input
                        type="text"
                        value={newSectorName}
                        onChange={(e) => setNewSectorName(e.target.value)}
                        placeholder="Enter sector name"
                        disabled={loading}
                        style={{ width: "100%", marginTop: "5px" }}
                      />
                    </div>
                    <div>
                      <label>Sector Description:</label>
                      <textarea
                        value={newSectorDescription}
                        onChange={(e) =>
                          setNewSectorDescription(e.target.value)
                        }
                        placeholder="Enter sector description"
                        disabled={loading}
                        rows={3}
                        style={{
                          width: "100%",
                          marginTop: "5px",
                          resize: "vertical",
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Step 2: Select or create industry within the sector */}
              {(selectedSector || createNewSector) && (
                <div style={{ marginTop: "20px" }}>
                  <label>Target Industry:</label>
                  {!createNewSector && loadingIndustries ? (
                    <div className="moveItemDialog__loading">
                      <CircularProgress size={20} />
                      <span>Loading available industries...</span>
                    </div>
                  ) : (
                    !createNewSector && (
                      <select
                        value={targetParent?.id || ""}
                        onChange={(e) => {
                          const selectedParent = availableIndustries.find(
                            (industry) => industry.id === e.target.value
                          );
                          setTargetParent(selectedParent);
                          setCreateNewParent(false); // Reset create new when selecting existing
                        }}
                        disabled={loading || createNewParent}
                      >
                        <option value="">
                          {availableIndustries.length === 0
                            ? "No industries available"
                            : "Select target industry..."}
                        </option>
                        {availableIndustries.map((industry) => (
                          <option key={industry.id} value={industry.id}>
                            {industry.name}
                          </option>
                        ))}
                      </select>
                    )
                  )}

                  {/* Option to create new industry */}
                  {!createNewSector && (
                    <div style={{ marginTop: "10px" }}>
                      <label>
                        <input
                          type="checkbox"
                          checked={createNewParent}
                          onChange={(e) => {
                            setCreateNewParent(e.target.checked);
                            if (e.target.checked) {
                              setTargetParent(null); // Clear existing selection
                            }
                          }}
                          disabled={loading}
                        />
                        <span style={{ marginLeft: "8px" }}>
                          Create new industry
                        </span>
                      </label>
                    </div>
                  )}

                  {/* Input for new industry */}
                  {(createNewParent || createNewSector) && (
                    <div
                      style={{
                        marginTop: "10px",
                        border: "1px solid #ddd",
                        padding: "10px",
                        borderRadius: "4px",
                      }}
                    >
                      <div style={{ marginBottom: "10px" }}>
                        <label>Industry Name:</label>
                        <input
                          type="text"
                          value={newParentName}
                          onChange={(e) => setNewParentName(e.target.value)}
                          placeholder="Enter industry name"
                          disabled={loading}
                          style={{ width: "100%", marginTop: "5px" }}
                        />
                      </div>
                      <div>
                        <label>Industry Description:</label>
                        <textarea
                          value={newParentDescription}
                          onChange={(e) =>
                            setNewParentDescription(e.target.value)
                          }
                          placeholder="Enter industry description"
                          disabled={loading}
                          rows={3}
                          style={{
                            width: "100%",
                            marginTop: "5px",
                            resize: "vertical",
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* New Collection Option - Only for main items moving from sectors to categories */}
          {isMainItem &&
            targetTab === "categories" &&
            currentTab === "sectors" && (
              <div className="moveItemDialog__field">
                <label>
                  <input
                    type="checkbox"
                    checked={createNewCollection}
                    onChange={(e) => setCreateNewCollection(e.target.checked)}
                    disabled={loading}
                  />
                  Create as new collection
                </label>
                {createNewCollection && (
                  <input
                    type="text"
                    placeholder="Enter new collection name"
                    value={newCollectionName}
                    onChange={(e) => setNewCollectionName(e.target.value)}
                    disabled={loading}
                    style={{ marginTop: "8px", width: "100%", padding: "8px" }}
                  />
                )}
              </div>
            )}

          {/* Move Preview */}
          {targetTab &&
            (isMainItem ||
              targetParent ||
              makeMasterSector ||
              createNewParent ||
              createNewSector ||
              selectedSector) && (
              <div className="moveItemDialog__preview">
                <h4>Move Preview:</h4>
                {isMainItem ? (
                  <p>
                    <strong>{item?.name}</strong> will be moved from{" "}
                    <strong>{collectionMapping[currentTab]?.label}</strong> to{" "}
                    <strong>{collectionMapping[targetTab]?.label}</strong>
                    {currentTab === "sectors" &&
                      targetTab === "sectors" &&
                      makeMasterSector && <> as a main sector</>}
                    {currentTab === "sectors" &&
                      targetTab === "sectors" &&
                      !makeMasterSector &&
                      targetParent && (
                        <>
                          {" "}
                          under <strong>{targetParent.name}</strong>
                        </>
                      )}
                    {createNewCollection && newCollectionName && (
                      <>
                        {" "}
                        and renamed to <strong>{newCollectionName}</strong>
                      </>
                    )}
                    .
                  </p>
                ) : (
                  <div>
                    <p>
                      <strong>{item?.name}</strong> will be moved from{" "}
                      <strong>{currentParent?.name}</strong> in{" "}
                      <strong>{collectionMapping[currentTab]?.label}</strong> to{" "}
                      {targetTab === "sectors" ? (
                        <span>
                          {createNewSector && newSectorName ? (
                            <>
                              <strong>new sector "{newSectorName}"</strong>
                              {createNewParent && newParentName && (
                                <>
                                  {" "}
                                  under{" "}
                                  <strong>
                                    new industry "{newParentName}"
                                  </strong>
                                </>
                              )}
                            </>
                          ) : selectedSector ? (
                            <>
                              <strong>sector "{selectedSector.name}"</strong>
                              {createNewParent && newParentName ? (
                                <>
                                  {" "}
                                  under{" "}
                                  <strong>
                                    new industry "{newParentName}"
                                  </strong>
                                </>
                              ) : targetParent ? (
                                <>
                                  {" "}
                                  under{" "}
                                  <strong>
                                    industry "{targetParent.name}"
                                  </strong>
                                </>
                              ) : (
                                ""
                              )}
                            </>
                          ) : (
                            <strong>
                              {collectionMapping[targetTab]?.label}
                            </strong>
                          )}
                        </span>
                      ) : (
                        <span>
                          {createNewParent && newParentName ? (
                            <strong>
                              new{" "}
                              {targetTab === "categories"
                                ? "category"
                                : "collection"}{" "}
                              "{newParentName}"
                            </strong>
                          ) : targetParent ? (
                            <strong>{targetParent.name}</strong>
                          ) : (
                            <strong>
                              {collectionMapping[targetTab]?.label}
                            </strong>
                          )}{" "}
                          in{" "}
                          <strong>{collectionMapping[targetTab]?.label}</strong>
                        </span>
                      )}
                      .
                    </p>

                    {/* Show what will be created */}
                    {(createNewSector || createNewParent) && (
                      <div
                        style={{
                          marginTop: "10px",
                          padding: "10px",
                          backgroundColor: "#f0f8ff",
                          borderRadius: "4px",
                        }}
                      >
                        <p
                          style={{
                            margin: "0 0 5px 0",
                            fontWeight: "bold",
                            color: "#2c5282",
                          }}
                        >
                          New items to be created:
                        </p>
                        {createNewSector && newSectorName && (
                          <p style={{ margin: "2px 0", fontSize: "0.9em" }}>
                            📁 New Sector: <strong>{newSectorName}</strong>
                            {newSectorDescription && (
                              <span style={{ color: "#666" }}>
                                {" "}
                                - {newSectorDescription}
                              </span>
                            )}
                          </p>
                        )}
                        {createNewParent && newParentName && (
                          <p style={{ margin: "2px 0", fontSize: "0.9em" }}>
                            📂 New{" "}
                            {targetTab === "sectors"
                              ? "Industry"
                              : targetTab === "categories"
                              ? "Category"
                              : "Collection"}
                            : <strong>{newParentName}</strong>
                            {newParentDescription && (
                              <span style={{ color: "#666" }}>
                                {" "}
                                - {newParentDescription}
                              </span>
                            )}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
        </div>

        <div
          className="moveItemDialog__actions"
          style={{
            flexShrink: 0,
            borderTop: "1px solid #e0e0e0",
            paddingTop: "16px",
            marginTop: "16px",
          }}
        >
          <button
            className="spbtn spbtn-sub"
            onClick={handleMove}
            disabled={
              loading ||
              !targetTab ||
              // For sub-items: sectors tab validation
              (!isMainItem &&
                targetTab === "sectors" &&
                ((!selectedSector && !createNewSector) ||
                  (createNewSector && !newSectorName.trim()) ||
                  (!targetParent && !createNewParent) ||
                  (createNewParent && !newParentName.trim()))) ||
              // For sub-items: non-sectors validation
              (!isMainItem &&
                targetTab !== "sectors" &&
                ((!targetParent && !createNewParent) ||
                  (createNewParent && !newParentName.trim()))) ||
              // For main items with new collection: need collection name
              (isMainItem &&
                createNewCollection &&
                !newCollectionName.trim()) ||
              // For main items moving within sectors without making main: need target parent
              (isMainItem &&
                targetTab === "sectors" &&
                !makeMasterSector &&
                !targetParent)
            }
          >
            {loading ? (
              <div className="button-content">
                <CircularProgress size={20} color="inherit" />
                <span style={{ marginLeft: "8px" }}>Moving...</span>
              </div>
            ) : (
              "Move Item"
            )}
          </button>
          <button
            className="spbtn spbtn-canc"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
        </div>
      </div>
    </SpDialog>
  );
};

export default MoveItemDialog;
